import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  ArchivePortalDocument,
  ChangePortalDocumentAssignments,
  DocumentMemberHit,
  GetPortalDocumentAdmin,
  GetPortalDocumentAssignments,
  PortalDocumentAdminDetail,
  PortalDocumentAssignments,
  PublishPortalDocument,
  RerenderPortalDocument,
  SearchDocumentMembers,
  SetPortalDocumentAudience,
  SetPortalDocumentDueDate,
  SetPortalDocumentReviewMode,
} from '../../services/dtos';

// D5 configure page — the vision's Form I (staff-side document setup): what
// members see it labeled as, whether it asks for review, who it goes to
// (all good-standing members or specific individuals), and an optional due
// date. Push style is fixed per the vision: notify + track, never block —
// publishing a review document notifies its audience, and the server
// guarantees nobody is ever notified twice or asked again after acknowledging.

const RENDER_POLL_MS = 2500;
const SEARCH_DEBOUNCE_MS = 300;

function previewUrl(id: number): string {
  return `${apiServices.apiUrl()}/admin/documents/${id}/view`;
}

function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

// Due dates are date-only values stored at UTC midnight — format and compare
// them in UTC so the shown day never drifts by timezone.
function fmtDueDate(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString([], { dateStyle: 'medium', timeZone: 'UTC' });
}

function Chip({
  tone,
  children,
}: {
  tone: 'green' | 'blue' | 'amber' | 'red' | 'gray';
  children: React.ReactNode;
}) {
  const tones = {
    green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    gray: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  };
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
      {children}
    </div>
  );
}

const DocumentConfigurePage: React.FC = () => {
  const { id } = useParams();
  const docId = Number(id);

  const [detail, setDetail] = useState<PortalDocumentAdminDetail | null>(null);
  const [assignments, setAssignments] = useState<PortalDocumentAssignments | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [due, setDue] = useState('');            // yyyy-mm-dd from the picker
  const dueInitialized = useRef(false);

  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<DocumentMemberHit[]>([]);

  const doc = detail?.document;

  const loadDetail = () =>
    apiServices.client
      .get(new GetPortalDocumentAdmin({ id: docId }))
      .then((d: PortalDocumentAdminDetail) => {
        setDetail(d);
        setError(null);
        if (!dueInitialized.current) {
          setDue(d.document.dueAtUtc ? d.document.dueAtUtc.slice(0, 10) : '');
          dueInitialized.current = true;
        }
      })
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Loading the document failed — try again.')
      );

  const loadAssignments = () =>
    apiServices.client
      .get(new GetPortalDocumentAssignments({ id: docId }))
      .then((a: PortalDocumentAssignments) => setAssignments(a))
      .catch(() => undefined);

  useEffect(() => {
    loadDetail();
    loadAssignments();
  }, [docId]);

  // keep the status chip moving while the render queue works
  const rendering =
    doc?.renderState === 'Queued' || doc?.renderState === 'Rendering';
  useEffect(() => {
    if (!rendering) return;
    const t = setInterval(loadDetail, RENDER_POLL_MS);
    return () => clearInterval(t);
  }, [rendering, docId]);

  // member picker: debounced server search
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setHits([]);
      return;
    }
    const t = setTimeout(
      () =>
        apiServices.client
          .get(new SearchDocumentMembers({ q }))
          .then((h: DocumentMemberHit[]) => setHits(h))
          .catch(() => setHits([])),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [search]);

  const run = (fn: () => Promise<any>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    fn()
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'That change failed — try again.')
      )
      .then(() => setBusy(false));
  };

  const applyDetail = (d: PortalDocumentAdminDetail) => setDetail(d);

  const publish = (unpublish: boolean) =>
    run(() =>
      apiServices.client
        .post(new PublishPortalDocument({ id: docId, unpublish }))
        .then(applyDetail)
    );

  const setReviewMode = (reviewMode: string) =>
    run(() =>
      apiServices.client
        .post(new SetPortalDocumentReviewMode({ id: docId, reviewMode }))
        .then(applyDetail)
    );

  const setAudience = (audience: string) =>
    run(() =>
      apiServices.client
        .post(new SetPortalDocumentAudience({ id: docId, audience }))
        .then(applyDetail)
    );

  const saveDue = (clear: boolean) =>
    run(() =>
      apiServices.client
        .post(
          new SetPortalDocumentDueDate({
            id: docId,
            dueAtUtc: clear || !due ? undefined : `${due}T00:00:00Z`,
          })
        )
        .then((d: PortalDocumentAdminDetail) => {
          applyDetail(d);
          setDue(d.document.dueAtUtc ? d.document.dueAtUtc.slice(0, 10) : '');
        })
    );

  const changeAssignments = (add?: string[], remove?: string[]) =>
    run(() =>
      apiServices.client
        .post(new ChangePortalDocumentAssignments({ id: docId, add, remove }))
        .then((a: PortalDocumentAssignments) => {
          setAssignments(a);
          setSearch('');
          setHits([]);
        })
    );

  const rerender = () =>
    run(() =>
      apiServices.client
        .post(new RerenderPortalDocument({ id: docId }))
        .then(applyDetail)
    );

  const archive = (unarchive: boolean) =>
    run(() =>
      apiServices.client
        .post(new ArchivePortalDocument({ id: docId, unarchive }))
        .then(applyDetail)
    );

  const assignedIds = useMemo(
    () =>
      new Set(
        (assignments?.rows || []).map((r) => r.uniqueMemberId.toLowerCase())
      ),
    [assignments]
  );

  const requiresReview =
    doc?.reviewMode === 'Acknowledge' || doc?.reviewMode === 'TypedName';

  return (
    <div className="mx-auto w-full max-w-4xl flex flex-col gap-4 dark:text-white">
      <div>
        <Link
          to="/doc-admin"
          className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          ← Document Admin
        </Link>
        {doc && (
          <>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="text-xl font-bold break-words">{doc.title}</h2>
              {doc.renderState === 'Ready' ? (
                <Chip tone="green">Ready</Chip>
              ) : doc.renderState === 'Failed' ? (
                <Chip tone="red">Failed</Chip>
              ) : (
                <Chip tone="amber">
                  {doc.renderState === 'Rendering'
                    ? `Rendering ${doc.renderedPages}/${doc.pageCount ?? '?'}`
                    : 'Queued'}
                </Chip>
              )}
              {doc.isPublished && <Chip tone="blue">Published</Chip>}
              {doc.isArchived && <Chip tone="gray">Archived</Chip>}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              {doc.docType && <span>{doc.docType}</span>}
              <span>
                {doc.pageCount ?? '—'} page{doc.pageCount === 1 ? '' : 's'}
              </span>
              <span>{fmtSize(doc.pdfBytes)}</span>
              <span>
                Uploaded by {doc.uploadedByName} · {fmtWhen(doc.createdAtUtc)}
              </span>
              {doc.renderState === 'Ready' && (
                <a
                  href={previewUrl(doc.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  Preview
                </a>
              )}
              {requiresReview && (
                <Link
                  to={`/doc-admin/roster/${doc.id}`}
                  className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  Roster
                </Link>
              )}
            </div>
          </>
        )}
      </div>

      {error && <Toast color="failure">{error}</Toast>}
      {!detail && !error && <Spinner color="info" />}

      {doc && (
        <>
          <Panel title="Visibility">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {doc.isPublished
                ? 'This document is live — its audience can read it now.'
                : 'Members can’t see this document until it’s published.'}
              {requiresReview &&
                !doc.isPublished &&
                ' Publishing a review document notifies its audience.'}
            </p>
            <div>
              <button
                onClick={() => publish(doc.isPublished)}
                disabled={
                  busy || (!doc.isPublished && (doc.renderState !== 'Ready' || doc.isArchived))
                }
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                {doc.isPublished ? 'Unpublish' : 'Publish'}
              </button>
            </div>
          </Panel>

          <Panel title="Review">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              What this document asks of its readers. Existing acknowledgments
              are permanent records and survive a mode change.
            </p>
            <select
              id="configure-review-mode"
              name="reviewMode"
              value={doc.reviewMode || 'None'}
              onChange={(e) => setReviewMode(e.target.value)}
              disabled={busy}
              className="w-fit rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
            >
              <option value="None">No review — just readable</option>
              <option value="Acknowledge">Acknowledge — one-click “I’ve reviewed this”</option>
              <option value="TypedName">Typed name — member types their full name</option>
              <option value="FormalSignature" disabled>
                Formal signature — coming later
              </option>
            </select>
          </Panel>

          <Panel title="Due date">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Optional target date — shown to members and used to flag who’s
              outstanding on the roster. It never blocks anyone’s portal
              access.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                id="configure-due-date"
                name="dueDate"
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
              />
              <button
                onClick={() => saveDue(false)}
                disabled={busy || !due}
                className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
              >
                Save
              </button>
              {doc.dueAtUtc && (
                <button
                  onClick={() => saveDue(true)}
                  disabled={busy}
                  className="text-sm font-medium text-gray-500 hover:underline dark:text-gray-400"
                >
                  Clear
                </button>
              )}
              {doc.dueAtUtc && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Currently due {fmtDueDate(doc.dueAtUtc)}
                </span>
              )}
            </div>
          </Panel>

          <Panel title="Audience">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Who this document goes to (Form I: all members in good standing,
              or specific individuals).
            </p>
            <div className="flex flex-col gap-2 text-sm">
              {[
                {
                  value: 'AllActive',
                  label: 'All good-standing members',
                },
                {
                  value: 'Specific',
                  label: 'Specific members only',
                },
              ].map((o) => (
                <label key={o.value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="audience"
                    value={o.value}
                    checked={doc.audience === o.value}
                    onChange={() => setAudience(o.value)}
                    disabled={busy}
                    className="border-gray-300 dark:border-gray-600"
                  />
                  {o.label}
                </label>
              ))}
            </div>

            {doc.audience === 'Specific' && (
              <div className="flex flex-col gap-3 border-t border-gray-100 dark:border-gray-800 pt-3">
                {(assignments?.rows.length ?? 0) === 0 && (
                  <div className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 p-3 text-sm text-amber-800 dark:text-amber-200">
                    Nobody is assigned yet — this document is invisible to
                    members until you add someone below.
                  </div>
                )}

                {(assignments?.rows.length ?? 0) > 0 && (
                  <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-700">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="px-3 py-2">Member</th>
                          <th className="px-3 py-2 w-36">Member #</th>
                          <th className="px-3 py-2 w-28">Status</th>
                          <th className="px-3 py-2 w-52">Assigned</th>
                          <th className="px-3 py-2 w-20"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {assignments!.rows.map((r) => (
                          <tr key={r.uniqueMemberId}>
                            <td className="px-3 py-1.5 text-gray-900 dark:text-white">
                              {r.name}
                            </td>
                            <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                              {r.uniqueMemberId}
                            </td>
                            <td className="px-3 py-1.5 text-gray-500 dark:text-gray-400">
                              {r.membershipStatus || '—'}
                            </td>
                            <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">
                              {fmtWhen(r.assignedAtUtc)} by {r.assignedByName}
                            </td>
                            <td className="px-3 py-1.5">
                              <button
                                onClick={() =>
                                  changeAssignments(undefined, [r.uniqueMemberId])
                                }
                                disabled={busy}
                                className="text-xs font-medium text-red-600 hover:underline dark:text-red-400"
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <input
                    id="assignment-search"
                    name="assignmentSearch"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Add a member — search name, email, or member #"
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm"
                  />
                  {hits.length > 0 && (
                    <div className="rounded border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                      {hits.map((h) => {
                        const already = assignedIds.has(
                          h.uniqueMemberId.toLowerCase()
                        );
                        return (
                          <div
                            key={h.uniqueMemberId}
                            className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                          >
                            <div>
                              <span className="text-gray-900 dark:text-white">
                                {h.name}
                              </span>
                              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                {h.uniqueMemberId}
                                {h.membershipStatus ? ` · ${h.membershipStatus}` : ''}
                              </span>
                            </div>
                            {already ? (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                Assigned
                              </span>
                            ) : (
                              <button
                                onClick={() =>
                                  changeAssignments([h.uniqueMemberId])
                                }
                                disabled={busy}
                                className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                              >
                                Add
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Panel>

          <div className="flex flex-wrap items-center gap-4 text-sm font-medium">
            {(doc.renderState === 'Ready' || doc.renderState === 'Failed') && (
              <button
                onClick={rerender}
                disabled={busy}
                className="text-indigo-600 hover:underline dark:text-indigo-400"
              >
                Re-render
              </button>
            )}
            <button
              onClick={() => archive(doc.isArchived)}
              disabled={busy}
              className="text-gray-500 hover:underline dark:text-gray-400"
            >
              {doc.isArchived ? 'Unarchive' : 'Archive'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentConfigurePage;
